import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { ActivityLoggerService } from 'src/app/_shared/service/activity-logger.service';
import constant from 'src/constant.json';
import { LoanComputationService } from '../../service/loan-computation.service';
import { MyHomeState } from '../../state/my-home.state';

@Component({
  selector: 'app-spend-forecast',
  templateUrl: './spend-forecast.component.html',
  styleUrls: ['./spend-forecast.component.scss'],
})
export class SpendForecastComponent implements OnInit {
  @Input() propertyValue;
  @Input() loanBalance;
  @Input() source;
  private debounceSubject = new Subject<number>();
  ltvRate = 80;
  private cashOutRefinanceValue = 0;

  spendForecastCalculation = {};
  spendForecastForm = new FormGroup({});
  cashOut = 0;
  tradeUpLimit = 0;
  addOnLimit = 0;
  isLoading = true;
  onDestroyNotifier$ = new Subject();
  loggerAddress: any;
  pmxPropertyId;
  currentEquity;
  constructor(
    private loanComputationService: LoanComputationService,
    private snackBar: MatSnackBar,
    public myHomeState: MyHomeState,
    private formBuilder: FormBuilder,
    private activityLoggerService: ActivityLoggerService,
  ) { }

  ngOnInit() {
    this.source = this.source ? this.source : null;
    this.myHomeState.activePropertyDetails$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res: any) => {
      if (res && res.basic) {
        this.pmxPropertyId = res.basic.PMXPropertyId;

        this.loggerAddress = {
          city: res.basic.City,
          state: res.basic.StateOrProvince,
          street: res.basic.StreetAddress,
          zip: res.basic.PostalCode,
        };
      }
    });
    this.spendForecastForm = this.formBuilder.group({
      equity: [null, []],
    });
    if (this.myHomeState.activeHomeValue && Number(this.myHomeState.activeHomeValue.equityUsed))
      this.spendForecastForm.controls.equity.setValue(this.myHomeState.activeHomeValue.equityUsed, {
        emitEvent: false,
      });
    if (!this.spendForecastForm.controls.equity.value) {
      this.spendForecastForm.controls.equity.setValue(null, { emitEvent: false });
      this.calculateTradeLimitInitially();
    }
    if (this.myHomeState.activeHomeValue && Number(this.myHomeState.activeHomeValue.loanToValueUpperLimitRatio))
      this.ltvRate = this.myHomeState.activeHomeValue.loanToValueUpperLimitRatio;
    this.spendForecastForm
      .get('equity')
      .valueChanges.pipe(takeUntil(this.onDestroyNotifier$))
      .subscribe((value) => {
        this.calculateTradeLimitOnEquityChange();
      });
    this.myHomeState.activeHomeValuesLoader$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((status) => {
      this.isLoading = status;
    });
    this.debounceSubject.pipe(debounceTime(1000)).subscribe(() => {
      this.logUserActivity(
        constant.LOGGER.myHome.category,
        constant.LOGGER.myHome.action.cashOutRefinance.key,
        this.pmxPropertyId,
        this.loggerAddress
      );
    });
  }

  isPositive(value) {
    return Math.sign(value) == -1 ? 0 : value;
  }

  ngOnChanges() {
    this.cashOut = 0;
    this.tradeUpLimit = 0;
    this.addOnLimit = 0;
    if (this.spendForecastForm && this.spendForecastForm.controls && this.spendForecastForm.controls.equity) {
      this.spendForecastForm.controls.equity.setValue(null, { emitEvent: false });
    }
    this.calculateTradeLimitInitially();
  }

  calculateTradeLimitInitially() {
    if (!this.propertyValue) {
      return;
    }

    if (!this.loanBalance) this.loanBalance = 0;
    this.spendForecastCalculation = this.loanComputationService.computeTradeLimit(
      this.propertyValue,
      this.loanBalance,
      this.ltvRate / 100,
      this.spendForecastForm &&
        this.spendForecastForm.controls &&
        this.spendForecastForm.controls.equity &&
        this.spendForecastForm.controls.equity.value
        ? this.spendForecastForm.controls.equity.value
        : 0
    );

    this.currentEquity = this.spendForecastCalculation['currentEquity'];

    if (this.spendForecastForm && this.spendForecastForm.controls && this.spendForecastForm.controls.equity) {
      this.spendForecastForm.controls.equity.setValue(this.spendForecastCalculation['currentEquity'], {
        emitEvent: false,
      });
    }

    this.tradeUpLimit = this.spendForecastCalculation['tradeUpLimit'];
    this.addOnLimit = this.spendForecastCalculation['addOnLimit'];
    this.cashOut = this.spendForecastCalculation['cashOut'];
  }

  calculateTradeLimitOnEquityChange() {
    if (!this.propertyValue) {
      return;
    }
    if (!this.loanBalance) this.loanBalance = 0;
    this.spendForecastCalculation = this.loanComputationService.computeTradeLimit(
      this.propertyValue,
      this.loanBalance,
      this.ltvRate / 100,
      this.spendForecastForm.controls.equity.value
    );
    this.tradeUpLimit = this.spendForecastCalculation['tradeUpLimit'];
    this.addOnLimit = this.spendForecastCalculation['addOnLimit'];
    this.updateMyHomeValuesInState();
  }

  cashOutRefinance(event: any) {
    this.cashOutRefinanceValue = event.value;
    this.debounceSubject.next(this.cashOutRefinanceValue);
  }

  calculateTradeLimitOnLTVRateChange() {
    if (!this.propertyValue) {
      return;
    }
    if (!this.loanBalance) this.loanBalance = 0;
    if (this.ltvRate < 0 || this.ltvRate > 100) {
      this.openSnackbar('Enter a valid rate', 'snackbar-warning');
      return;
    }
    this.spendForecastCalculation = this.loanComputationService.computeTradeLimit(
      this.propertyValue,
      this.loanBalance,
      this.ltvRate / 100,
      this.spendForecastForm.controls.equity.value
    );
    this.cashOut = this.spendForecastCalculation['cashOut'];
    this.tradeUpLimit = this.spendForecastCalculation['tradeUpLimit'];
    this.addOnLimit = this.spendForecastCalculation['addOnLimit'];
    this.updateMyHomeValuesInState();
  }

  logUserActivity(category, action, pmxPropertyId, address) {
    let loggerParams = {
      category: category,
      action: action,
    };
    let inputData = {
      pmxPropertyId: pmxPropertyId,
      address: address,
    };
    this.activityLoggerService.logActivity(loggerParams, inputData);
  }

  updateMyHomeValuesInState() {
    let myHomeValues = this.myHomeState.myHomeListValue.filter((myHome) => {
      return myHome.id == this.myHomeState.activeHomeValue.id;
    });
    myHomeValues = myHomeValues[0];
    if (myHomeValues.PMXPropertyId == this.myHomeState.activeHomeValue.PMXPropertyId) {
      (myHomeValues.equityUsed = this.spendForecastForm.controls.equity.value),
        (myHomeValues.loanToValueUpperLimitRatio = this.ltvRate);
    }
    this.myHomeState.homeListUpdateValue = myHomeValues;
  }

  openSnackbar(message, style) {
    this.snackBar.open(message, '', {
      duration: 3000,
      panelClass: [style],
      verticalPosition: 'top',
    });
  }
  ngOnDestroy() {
    this.onDestroyNotifier$.next();
    this.onDestroyNotifier$.complete();
  }

  buySecondHomeArticleSheet() {
    this.myHomeState.isArticleSheetOpenedValue = "buySecondHome";
  }
}
